import React, { useEffect, useMemo, useReducer } from 'react';
import { IApiTradingPlatformType, IApiUserTradingAccountType } from '@api/interfaces/data-contracts';
import { IApiSelfTradingAccountTypeViewModel } from '@api/interfaces/data-contracts';
import { apiInvestorAccount } from '@api/services/investorAccount.apiService';
import { apiMasterAccount } from '@api/services/masterAccount.apiService';
import { apiTradingAccountTypeUser } from '@api/services/tradingAccountTypeUser.apiService';
import { apiTradingAccountUserSettings } from '@api/services/tradingAccountUserSettings.apiService';
import { apiTradingPlatform } from '@api/services/tradingPlatform.apiService';
import { AccountCreateModal } from '@components-member/TradingAccounts/AccountCreate/AccountCreateModal';
import { AccountCreateContextProvider } from '@components-member/TradingAccounts/AccountCreate/context/accountCreateContext';
import { ACCOUNT_CREATE_ACTION_TYPE_ENUM } from '@components-member/TradingAccounts/AccountCreate/enums';
import { accountLimitForUnverifiedUsersExceeded } from '@components-member/TradingAccounts/AccountCreate/helpers';
import {
  accountCreateReducer,
  accountCreateState,
} from '@components-member/TradingAccounts/AccountCreate/state/accountCreateReducer';
import { AvailableOpenAccountType } from '@components-member/TradingAccounts/interfaces';
import { keyMap } from '@locales/lokalise/keyMap';
import { handleError } from '@utils/errors';
import { reactRouterService } from '@utils/library/reactRouter.service';
import { QUERY_PARAMS, TRADING_ACCOUNT_FORM_TYPE, TRADING_ACCOUNT_SOURCE } from 'global.enums';
import { observer } from 'mobx-react';
import { useMobxStore } from 'store/mobx';
import { IAccountCreationQueryParams, useAccountCreationQueryParams } from '../hooks/useAccountCreationQueryParams';

type AccountTypes = {
  selfTradeTypes: IApiSelfTradingAccountTypeViewModel[];
  masterTypes: IApiUserTradingAccountType[];
  investorTypes: IApiUserTradingAccountType[];
};

const getAvailableAccountPlatform = (
  availablePlatform: Set<IApiTradingPlatformType>,
  accountTypes: AccountTypes,
  source: TRADING_ACCOUNT_SOURCE,
  openAccountType: AvailableOpenAccountType,
): void => {
  const accountTypeMap: Record<
    AvailableOpenAccountType,
    IApiUserTradingAccountType[] | IApiSelfTradingAccountTypeViewModel[]
  > = {
    [TRADING_ACCOUNT_FORM_TYPE.MASTER]: accountTypes.masterTypes,
    [TRADING_ACCOUNT_FORM_TYPE.INVESTOR]: accountTypes.investorTypes,
    [TRADING_ACCOUNT_FORM_TYPE.SELF]: accountTypes.selfTradeTypes,
  };

  if (source === TRADING_ACCOUNT_SOURCE.SIDEBAR && accountTypeMap[openAccountType]?.length) {
    accountTypeMap[openAccountType].forEach((type) => {
      availablePlatform.add(type.tradingPlatformType);
    });
  } else {
    Object.values(accountTypeMap).forEach((accountTypeArray) => {
      accountTypeArray?.forEach((accountType) => {
        availablePlatform.add(accountType.tradingPlatformType);
      });
    });
  }
};

interface IAccountCreateProvider {
  closeModalHandler: () => void;
}

export const AccountCreateDataProvider = observer(({ closeModalHandler }: IAccountCreateProvider) => {
  const [state, dispatch] = useReducer(accountCreateReducer, accountCreateState);
  const { selfTradingTypes, investorTrading, masterTrading, creationSetting } = state;
  const { accountStore, tradingAccountsStore } = useMobxStore();
  const userProfileStatus = accountStore.userProfile?.profile?.status;
  const { onboardingConfiguration, onboardingConfigurationLoading, generalSettingsLoading, userProfileLoading } =
    accountStore;
  const isInitialDataBeingLoadedIntoState =
    generalSettingsLoading || onboardingConfigurationLoading || userProfileLoading;
  const isSocialTradingEnabled = !!onboardingConfiguration?.showCopyTradingTab;
  const { openAccountType, source, contestId, platformType } = useAccountCreationQueryParams(
    onboardingConfiguration?.showCopyTradingTab,
  );
  const isAccountLimitForUnverifiedUsers = accountLimitForUnverifiedUsersExceeded(
    userProfileStatus,
    openAccountType,
    creationSetting,
  );

  const setSelectedPlatformType = (platformType: IAccountCreationQueryParams['platformType']) => {
    reactRouterService.addUrlQueryParams({ [QUERY_PARAMS.PLATFORM_TYPE]: platformType });
  };

  useEffect(() => {
    void (async () => {
      try {
        const [{ data: creationSettings }, { data: brandingSettings }] = await Promise.all([
          apiTradingAccountUserSettings.getAccountCreationSettings(),
          apiTradingPlatform.getTradingPlatformBrandingSettings(),
        ]);

        dispatch({
          type: ACCOUNT_CREATE_ACTION_TYPE_ENUM.CREATION_SETTING,
          payload: { ...creationSettings, tradingPlatformBrandingSettings: brandingSettings },
        });
      } catch (error) {
        handleError(keyMap.tradingAccountsFailedToFetchTradingAccounts, error);
      }
    })();
  }, []);

  useEffect(() => {
    void (async () => {
      if (isInitialDataBeingLoadedIntoState) {
        dispatch({ type: ACCOUNT_CREATE_ACTION_TYPE_ENUM.PROCESSING, payload: false });

        return;
      }

      try {
        dispatch({ type: ACCOUNT_CREATE_ACTION_TYPE_ENUM.PROCESSING, payload: true });

        const { data: selfTradeTypes } = await apiTradingAccountTypeUser.getSelfTradingTypes({ contestId });
        let investorTypes: IApiUserTradingAccountType[] = [];
        let masterTypes: IApiUserTradingAccountType[] = [];

        if (isSocialTradingEnabled && !contestId) {
          const [masterResponse, investorResponse] = await Promise.all([
            apiMasterAccount.getInitialDataList(),
            apiInvestorAccount.getTypes(),
          ]);

          masterTypes = masterResponse.data;
          investorTypes = investorResponse.data;
        }

        dispatch({ type: ACCOUNT_CREATE_ACTION_TYPE_ENUM.SELF_TRADING_TYPES, payload: selfTradeTypes });
        dispatch({ type: ACCOUNT_CREATE_ACTION_TYPE_ENUM.INVESTOR_TRADING_TYPES, payload: investorTypes });
        dispatch({ type: ACCOUNT_CREATE_ACTION_TYPE_ENUM.MASTER_TRADING_TYPES, payload: masterTypes });
      } catch (error) {
        handleError(keyMap.tradingAccountsFailedToFetchTradingAccounts, error);
      } finally {
        dispatch({ type: ACCOUNT_CREATE_ACTION_TYPE_ENUM.PROCESSING, payload: false });
      }
    })();
  }, [contestId, isInitialDataBeingLoadedIntoState, isSocialTradingEnabled]);

  const availablePlatform = useMemo(() => new Set<IApiTradingPlatformType>(), []);

  getAvailableAccountPlatform(
    availablePlatform,
    {
      masterTypes: masterTrading,
      investorTypes: investorTrading,
      selfTradeTypes: selfTradingTypes,
    },
    source,
    openAccountType,
  );

  const refresh = tradingAccountsStore.fetchInitialAccountsData;

  const capitalizeSearchQuery = <T extends typeof platformType>(value: T) =>
    (value.charAt(0).toUpperCase() + value.slice(1)) as T;

  const providerProps = {
    isAccountLimitForUnverifiedUsers,
    formOpeningSource: source,
    openAccountType,
    accountCreateState: state,
    availablePlatform,
    selectedPlatformType: capitalizeSearchQuery(platformType),
    contestId,
    setSelectedPlatformType,
    updateActionState: dispatch,
    refresh,
  };

  const isProcessing = isInitialDataBeingLoadedIntoState || state.processing;

  return (
    <AccountCreateContextProvider {...providerProps}>
      <AccountCreateModal processing={isProcessing} closeModalHandler={closeModalHandler} />
    </AccountCreateContextProvider>
  );
});
